import React, { useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ChevronDown, ChevronUp, ChevronsUpDown, Mail } from "lucide-react";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> {
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  labelClassName?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightElement?: React.ReactNode;
  prefixText?: string;
  suffixText?: string;
  containerClassName?: string;
  inputClassName?: string;
  multiline?: boolean;
  rows?: number;
  as?: "input" | "textarea" | "select";
  options?: Array<{ label: string; value: string | number; icon?: React.ReactNode }>;
  showStepper?: boolean;
  onStepUp?: () => void;
  onStepDown?: () => void;
}

export const Input = forwardRef<any, InputProps>(
  (
    {
      label,
      subLabel,
      labelClassName = "",
      error,
      helperText,
      leftIcon,
      rightIcon,
      rightElement,
      prefixText,
      suffixText,
      type = "text",
      className = "",
      containerClassName = "",
      inputClassName = "",
      disabled,
      required,
      multiline = false,
      rows = 3,
      as = "input",
      options,
      showStepper = false,
      onStepUp,
      onStepDown,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    const actualType = isPassword ? (showPassword ? "text" : "password") : type;
    const hasError = Boolean(error);
    const isSelect = as === "select" || type === "select";
    const isTextarea = multiline || as === "textarea" || type === "textarea";

    return (
      <div className={`cht-flex cht-flex-col cht-w-full ${containerClassName}`}>
        {/* Label Header */}
        {(label || subLabel) && (
          <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5 cht-px-0.5">
            {label && (
              <label
                htmlFor={props.id || props.name}
                className={`cht-flex cht-items-center cht-gap-1 ${
                  labelClassName ? labelClassName : "cht-text-xs cht-font-bold cht-text-[#1e293b]"
                }`}
              >
                <span>{label}</span>
                {required && <span className="cht-text-red-500 cht-text-sm leading-none">*</span>}
              </label>
            )}
            {subLabel && (
              <span className="cht-text-[11px] cht-font-medium cht-text-gray-400">
                {subLabel}
              </span>
            )}
          </div>
        )}

        {/* Input Container */}
        <div
          className={`
            cht-relative cht-flex cht-items-center cht-w-full cht-bg-white cht-rounded-2xl
            cht-border cht-border-solid cht-overflow-hidden cht-transition-all cht-duration-200
            cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]
            ${
              hasError
                ? "cht-border-red-400 focus-within:cht-border-red-500 cht-bg-red-50/10 focus-within:cht-shadow-[0_6px_24px_rgba(239,68,68,0.18),0_1px_3px_rgba(0,0,0,0.06)]"
                : "cht-border-slate-200 hover:cht-border-slate-300 hover:cht-shadow-[0_2px_6px_rgba(0,0,0,0.08)] focus-within:cht-border-[#3e57da] focus-within:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)]"
            }
            ${disabled ? "cht-opacity-60 cht-bg-gray-50 cht-cursor-not-allowed cht-shadow-none" : ""}
            ${className}
          `}
          style={{ borderStyle: "solid" }}
        >
          {/* Left Icon (Leading Accessory) */}
          {leftIcon && (
            <div className="cht-flex cht-items-center cht-justify-center cht-pl-4 cht-pr-1 cht-text-[#3e57da] cht-transition-colors flex-shrink-0 select-none">
              {leftIcon}
            </div>
          )}

          {/* Prefix Text (e.g. (+1) or currency) */}
          {prefixText && (
            <span className="cht-pl-3 cht-pr-1 cht-text-sm cht-font-bold cht-text-gray-400 select-none flex-shrink-0">
              {prefixText}
            </span>
          )}

          {/* Input / Select / Textarea Element */}
          {isTextarea ? (
            <textarea
              ref={ref}
              rows={rows}
              disabled={disabled}
              required={required}
              className={`
                cht-w-full cht-bg-transparent cht-py-3.5 cht-px-4 cht-text-sm cht-font-medium
                cht-text-[#1e293b] placeholder:cht-text-gray-400 placeholder:cht-font-normal
                cht-border-none cht-outline-none cht-resize-none cht-shadow-none
                focus:cht-outline-none focus:cht-ring-0 focus:cht-border-none
                ${inputClassName}
              `}
              style={{ border: "none", outline: "none", boxShadow: "none", backgroundColor: "transparent" }}
              {...(props as any)}
            />
          ) : isSelect ? (
            <select
              ref={ref}
              disabled={disabled}
              required={required}
              className={`
                cht-w-full cht-bg-transparent cht-py-3.5 cht-px-4 cht-text-sm cht-font-medium
                cht-text-[#1e293b] cht-border-none cht-outline-none cht-appearance-none cht-cursor-pointer cht-shadow-none
                focus:cht-outline-none focus:cht-ring-0 focus:cht-border-none
                ${inputClassName}
              `}
              style={{ border: "none", outline: "none", boxShadow: "none", backgroundColor: "transparent" }}
              {...(props as any)}
            >
              {options ? (
                options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))
              ) : (
                props.children
              )}
            </select>
          ) : (
            <input
              ref={ref}
              type={actualType}
              disabled={disabled}
              required={required}
              className={`
                cht-w-full cht-bg-transparent cht-py-3.5 cht-px-4 cht-text-sm cht-font-medium
                cht-text-[#1e293b] placeholder:cht-text-gray-400 placeholder:cht-font-normal
                cht-border-none cht-outline-none cht-shadow-none
                focus:cht-outline-none focus:cht-ring-0 focus:cht-border-none
                ${inputClassName}
              `}
              style={{ border: "none", outline: "none", boxShadow: "none", backgroundColor: "transparent" }}
              {...(props as any)}
            />
          )}

          {/* Suffix Text */}
          {suffixText && (
            <span className="cht-pr-3 cht-text-sm cht-font-medium cht-text-gray-400 select-none flex-shrink-0">
              {suffixText}
            </span>
          )}

          {/* Stepper Controls (for Budget / Number) */}
          {showStepper && (
            <div className="cht-flex cht-flex-col cht-pr-2 cht-text-gray-400 flex-shrink-0">
              <button
                type="button"
                onClick={onStepUp}
                tabIndex={-1}
                className="hover:cht-text-gray-700 active:cht-scale-90 cht-transition-all p-0.5 cht-bg-transparent cht-border-none"
                style={{ border: "none", background: "transparent", outline: "none", boxShadow: "none" }}
              >
                <ChevronUp size={12} />
              </button>
              <button
                type="button"
                onClick={onStepDown}
                tabIndex={-1}
                className="hover:cht-text-gray-700 active:cht-scale-90 cht-transition-all p-0.5 cht-bg-transparent cht-border-none"
                style={{ border: "none", background: "transparent", outline: "none", boxShadow: "none" }}
              >
                <ChevronDown size={12} />
              </button>
            </div>
          )}

          {/* Select Dropdown Chevron */}
          {isSelect && (
            <div className="cht-pr-4 cht-text-gray-400 pointer-events-none flex-shrink-0">
              <ChevronDown size={16} />
            </div>
          )}

          {/* Password Show/Hide Toggle */}
          {isPassword && !disabled && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              className="cht-pr-4 cht-text-gray-400 hover:cht-text-gray-600 active:cht-scale-95 cht-transition-all flex-shrink-0 focus:cht-outline-none cht-bg-transparent cht-border-none"
              style={{ border: "none", background: "transparent", outline: "none", boxShadow: "none" }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}

          {/* Right Icon */}
          {rightIcon && !isPassword && (
            <div className="cht-pr-4 cht-text-gray-400 flex-shrink-0 flex items-center justify-center">
              {rightIcon}
            </div>
          )}

          {/* Custom Right Element (e.g. Card Brand Logo, Currency Badge, Calendar) */}
          {rightElement && (
            <div className="cht-pr-3 cht-flex cht-items-center cht-justify-center flex-shrink-0">
              {rightElement}
            </div>
          )}
        </div>

        {/* Helper text or Error message */}
        {(hasError || helperText) && (
          <div className="cht-min-h-[18px] cht-mt-1 cht-px-1">
            <AnimatePresence mode="wait">
              {hasError ? (
                <motion.p
                  key="error"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="cht-text-[11px] cht-font-bold cht-text-red-500 cht-flex cht-items-center cht-gap-1"
                >
                  <span>{error}</span>
                </motion.p>
              ) : helperText ? (
                <p key="helper" className="cht-text-[11px] cht-font-medium cht-text-gray-400">
                  {helperText}
                </p>
              ) : null}
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export const InputMail = forwardRef<any, InputProps>(
  (
    {
      label = "Email Address",
      labelClassName = "cht-text-[10px] cht-font-black cht-text-white/70 cht-uppercase cht-tracking-widest",
      className = "",
      leftIcon,
      type = "email",
      placeholder = "name@company.com",
      ...props
    },
    ref
  ) => {
    return (
      <Input
        ref={ref}
        type={type}
        label={label}
        labelClassName={labelClassName}
        leftIcon={leftIcon || <Mail size={18} />}
        placeholder={placeholder}
        className={className}
        {...props}
      />
    );
  }
);
InputMail.displayName = "InputMail";

export default Input;
